import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { NumericFormat } from 'react-number-format';
import Modal from '../../components/modal/modal';
import Card from '../../components/card/Card';
import Button from '../../components/button/Button';
import P from '../../components/texts/p/P';
import H from '../../components/texts/h/H';
import TextArea from '../../components/textarea/TextArea';
import DropDown from '../../components/dropdown/DropDown';
import Input from '../../components/input/Input';
import Icon from '../../components/icon/Icon';
import Toggle from '../../components/toggle/Toggle';
import { useClientTheme } from '../../context/ThemeContext';
import { useConfig } from '../../context/ConfigContext';
import type { SgrCatalogItem, SgrDistributionItem } from '../../types/interfaces';
import type { MultiSgrBaseProduct } from '../../services/ApplicationService';
import type { SelectableProductLine } from '../../utils/productLines';
import { productLineDropdownTitle } from '../../utils/productLines';
import { formatCurrency } from '../../utils/Masks';
import { APPLICATION_STATUS, CURRENCY_CODES } from '../../utils/Constants';
import {
  buildOriginationCurrencyDropdownItems,
  hasHistoricUsdProducts,
  isUsdHistoricLocked,
  isUsdSelectableForOriginationCurrencyDropdown,
  resolveLocalCurrencyCode,
  resolveUsdAvailable,
  USD_HISTORIC_LOCK_BANNER_TEXT,
} from '../../utils/quotationAssets';

const round2 = (n: number) => parseFloat((Number.isFinite(n) ? n : 0).toFixed(2));

const serializeBlockProducts = (blocks: SgrBlockState[]): string =>
  JSON.stringify(
    blocks.flatMap((b) =>
      b.products.map((p) => ({
        product_line_code: p.product_line_code,
        currency_code: p.currency_code,
        amount: p.amount,
      }))
    )
  );

export interface MultiSgrApprovalSubmitResult {
  comment: string;
  statusCode: string;
  approved_products: {
    products: Array<{
      product_line_code: string;
      amount: { currency_code: string; value: number };
    }>;
    /** Soft-rename: local currency total bucket (ARS|UYU); JSON key `*_ars` unchanged. */
    manual_approved_total_amount_ars: number;
    manual_approved_total_amount_usd: number;
  };
  sgr_distributions: SgrDistributionItem[];
}

interface ProductRowState {
  id: string;
  product_line_code: string;
  currency_code: string;
  amount: number;
}

interface SgrBlockState {
  id: string;
  sgr: SgrCatalogItem;
  products: ProductRowState[];
  toggleManual: boolean;
  /** Soft-rename: UI/state local-bucket total; not Argentina-only. */
  manualAmountArs: number;
  manualAmountUsd: number;
  isExpanded: boolean;
}

export interface MultiSgrApprovalModalProps {
  open: boolean;
  onClose: () => void;
  catalog: SgrCatalogItem[];
  baseProducts: MultiSgrBaseProduct[];
  productLines: SelectableProductLine[];
  isInstrumentationEnabled: boolean;
  initialComment?: string;
  onSubmit: (result: MultiSgrApprovalSubmitResult) => void;
}

const resolveBlockRows = (
  block: SgrBlockState,
): Array<{ product_line_code: string; currency_code: string; value: number }> => {
  if (block.products.length === 0) return [];
  return block.products.map((p) => ({
    product_line_code: p.product_line_code,
    currency_code: p.currency_code,
    value: round2(typeof p.amount === 'number' ? p.amount : parseFloat(String(p.amount)) || 0),
  }));
};

const blockTotals = (
  block: SgrBlockState,
  localCurrencyCode: string,
): { ars: number; usd: number } => {
  if (!block.toggleManual) {
    let ars = 0;
    let usd = 0;
    for (const p of block.products) {
      const v = typeof p.amount === 'number' ? p.amount : parseFloat(String(p.amount)) || 0;
      if (p.currency_code === localCurrencyCode) ars += v;
      else if (p.currency_code === CURRENCY_CODES.usd) usd += v;
    }
    return { ars: round2(ars), usd: round2(usd) };
  }
  return {
    // soft-rename: manualAmountArs = local bucket
    ars: round2(block.manualAmountArs),
    usd: round2(block.manualAmountUsd),
  };
};

/** Non-Movil rows with value 0 are omitted unless manual block total needs transport metadata. */
const shouldIncludeDistributionRow = (
  block: SgrBlockState,
  row: { currency_code: string; value: number },
  totals: { ars: number; usd: number },
  localCurrencyCode: string,
): boolean => {
  if (block.sgr.is_movil) return true;
  if (row.value > 0) return true;
  if (!block.toggleManual) return false;
  if (row.currency_code === localCurrencyCode && totals.ars > 0) return true;
  if (row.currency_code === CURRENCY_CODES.usd && totals.usd > 0) return true;
  return false;
};

const blockHasPositiveAmount = (block: SgrBlockState, localCurrencyCode: string): boolean => {
  const { ars, usd } = blockTotals(block, localCurrencyCode);
  return ars > 0 || usd > 0;
};

function MultiSgrApprovalModal({
  open,
  onClose,
  catalog,
  baseProducts,
  productLines,
  isInstrumentationEnabled,
  initialComment = '',
  onSubmit,
}: MultiSgrApprovalModalProps) {
  const { clientTheme } = useClientTheme();
  const config = useConfig();
  const localCurrencyCode = resolveLocalCurrencyCode(config);
  const isQuotationsEnabled = config.isQuotationsEnabled ?? false;
  const idSeq = useRef(0);
  const initialProductsSnapshotRef = useRef('');
  const nextId = useCallback((prefix: string) => {
    idSeq.current += 1;
    return `${prefix}-${idSeq.current}`;
  }, []);

  const [blocks, setBlocks] = useState<SgrBlockState[]>([]);
  const [comment, setComment] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [usdQuotationAvailable, setUsdQuotationAvailable] = useState<boolean | null>(null);

  const enabledCatalog = useMemo(
    () => catalog.filter((c) => c.enabled !== false),
    [catalog],
  );

  const movilCatalogEntry = useMemo(() => {
    return (
      enabledCatalog.find((c) => c.is_movil) ?? enabledCatalog[0] ?? null
    );
  }, [enabledCatalog]);

  const defaultProductCode = productLines[0]?.code ?? '';

  useEffect(() => {
    if (!open || !isQuotationsEnabled) {
      setUsdQuotationAvailable(null);
      return;
    }
    const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
    Promise.all([
      fetch('/v1/private/applications/quotations', { headers }).then((res) => res.json()),
      fetch('/v1/private/applications/quotations/USD?page=0&size=1', { headers }).then((res) =>
        res.ok ? res.json() : null
      ),
    ])
      .then(([data, usdPage]) => {
        const assets = Array.isArray(data) ? data : data?.data || [];
        setUsdQuotationAvailable(isUsdSelectableForOriginationCurrencyDropdown(usdPage, assets));
      })
      .catch(() => setUsdQuotationAvailable(false));
  }, [open, isQuotationsEnabled]);

  const usdAvailable = useMemo(
    () =>
      resolveUsdAvailable(
        {
          isQuotationsEnabled,
          flagUsdCurrencyEnabled: config.flagUsdCurrencyEnabled,
        },
        usdQuotationAvailable
      ),
    [isQuotationsEnabled, config.flagUsdCurrencyEnabled, usdQuotationAvailable]
  );

  const currencyOptions = useMemo(
    () =>
      buildOriginationCurrencyDropdownItems(
        localCurrencyCode,
        CURRENCY_CODES.usd,
        usdAvailable
      ),
    [localCurrencyCode, usdAvailable]
  );

  const hasHistoricUsd = useMemo(
    () =>
      hasHistoricUsdProducts(
        baseProducts.map((bp) => ({
          currency_code: bp.currency_code || localCurrencyCode,
        }))
      ),
    [baseProducts, localCurrencyCode]
  );

  const usdHistoricLocked = useMemo(
    () => isUsdHistoricLocked(usdAvailable, hasHistoricUsd),
    [usdAvailable, hasHistoricUsd]
  );

  useEffect(() => {
    if (!open || !movilCatalogEntry) return;
    const rows: ProductRowState[] =
      baseProducts.length > 0
        ? baseProducts.map((bp) => ({
            id: nextId('row'),
            product_line_code: bp.product_line_code || defaultProductCode,
            currency_code: bp.currency_code || localCurrencyCode,
            amount: bp.amount ?? 0,
          }))
        : [
            {
              id: nextId('row'),
              product_line_code: defaultProductCode,
              currency_code: localCurrencyCode,
              amount: 0,
            },
          ];

    setBlocks([
      {
        id: nextId('blk'),
        sgr: movilCatalogEntry,
        products: rows,
        toggleManual: false,
        manualAmountArs: 0,
        manualAmountUsd: 0,
        isExpanded: true,
      },
    ]);
    initialProductsSnapshotRef.current = serializeBlockProducts([
      {
        id: 'snapshot',
        sgr: movilCatalogEntry,
        products: rows,
        toggleManual: false,
        manualAmountArs: 0,
        manualAmountUsd: 0,
        isExpanded: true,
      },
    ]);
    setComment(initialComment);
    setSubmitError('');
  }, [open, movilCatalogEntry, baseProducts, defaultProductCode, initialComment, nextId, localCurrencyCode]);

  const usedSgrCodes = useMemo(() => blocks.map((b) => b.sgr.code), [blocks]);

  const canAddSgr = useMemo(() => {
    const set = new Set(usedSgrCodes);
    return enabledCatalog.some((c) => !set.has(c.code));
  }, [enabledCatalog, usedSgrCodes]);

  const duplicateSgr = useMemo(
    () => new Set(usedSgrCodes).size !== usedSgrCodes.length,
    [usedSgrCodes],
  );

  const anyPositiveTotal = useMemo(
    () => blocks.some((b) => blockHasPositiveAmount(b, localCurrencyCode)),
    [blocks, localCurrencyCode],
  );

  const formValid =
    blocks.length > 0 &&
    !duplicateSgr &&
    anyPositiveTotal &&
    blocks.every((b) => {
      if (!b.toggleManual) return true;
      if (b.manualAmountArs > 0) {
        if (!b.products.some((p) => p.currency_code === localCurrencyCode)) return false;
      }
      if (b.manualAmountUsd > 0) {
        if (!b.products.some((p) => p.currency_code === 'USD')) return false;
      }
      return true;
    });

  const grandTotals = useMemo(() => {
    let ars = 0;
    let usd = 0;
    for (const b of blocks) {
      const t = blockTotals(b, localCurrencyCode);
      ars += t.ars;
      usd += t.usd;
    }
    return { ars: round2(ars), usd: round2(usd) };
  }, [blocks]);

  const productLineItems = useMemo(
    () => productLines.map((pl) => ({ label: pl.label })),
    [productLines],
  );

  const addSgrBlock = () => {
    const taken = new Set(blocks.map((b) => b.sgr.code));
    const next = enabledCatalog.find((c) => !taken.has(c.code));
    if (!next) return;
    setBlocks((prev) => [
      ...prev,
      {
        id: nextId('blk'),
        sgr: next,
        products:
          baseProducts.length > 0
            ? baseProducts.map((bp) => ({
                id: nextId('row'),
                product_line_code: bp.product_line_code || defaultProductCode,
                currency_code: bp.currency_code || localCurrencyCode,
                amount: 0,
              }))
            : [
                {
                  id: nextId('row'),
                  product_line_code: defaultProductCode,
                  currency_code: localCurrencyCode,
                  amount: 0,
                },
              ],
        toggleManual: false,
        manualAmountArs: 0,
        manualAmountUsd: 0,
        isExpanded: true,
      },
    ]);
  };

  const toggleExpand = (blockId: string) => {
    setBlocks((prev) =>
      prev.map((b) => (b.id === blockId ? { ...b, isExpanded: !b.isExpanded } : b)),
    );
  };

  const removeBlock = (blockId: string) => {
    setBlocks((prev) => {
      const target = prev.find((b) => b.id === blockId);
      if (!target || prev.length <= 1) return prev;
      if (target.sgr.is_movil) return prev;
      return prev.filter((b) => b.id !== blockId);
    });
  };

  const updateBlock = (blockId: string, patch: Partial<SgrBlockState>) => {
    setBlocks((prev) =>
      prev.map((b) => (b.id === blockId ? { ...b, ...patch } : b)),
    );
  };

  const updateProductRow = (
    blockId: string,
    rowId: string,
    patch: Partial<ProductRowState>,
  ) => {
    setBlocks((prev) =>
      prev.map((b) => {
        if (b.id !== blockId) return b;
        return {
          ...b,
          products: b.products.map((r) =>
            r.id === rowId ? { ...r, ...patch } : r,
          ),
        };
      }),
    );
  };

  const addProductRow = (blockId: string) => {
    setBlocks((prev) =>
      prev.map((b) => {
        if (b.id !== blockId) return b;
        return {
          ...b,
          products: [
            ...b.products,
            {
              id: nextId('row'),
              product_line_code: defaultProductCode,
              currency_code: localCurrencyCode,
              amount: 0,
            },
          ],
        };
      }),
    );
  };

  const removeProductRow = (blockId: string, rowId: string) => {
    setBlocks((prev) =>
      prev.map((b) => {
        if (b.id !== blockId) return b;
        if (b.products.length <= 1) return b;
        return {
          ...b,
          products: b.products.filter((r) => r.id !== rowId),
        };
      }),
    );
  };

  const handlePrimary = () => {
    if (!formValid || !movilCatalogEntry) return;

    if (
      usdHistoricLocked &&
      serializeBlockProducts(blocks) !== initialProductsSnapshotRef.current
    ) {
      setSubmitError(
        'No podés modificar productos en USD mientras la moneda está deshabilitada para este cliente.'
      );
      return;
    }

    setSubmitError('');

    const sgr_distributions: SgrDistributionItem[] = [];
    for (const b of blocks) {
      const totals = blockTotals(b, localCurrencyCode);
      for (const r of resolveBlockRows(b)) {
        if (!shouldIncludeDistributionRow(b, r, totals, localCurrencyCode)) continue;
        sgr_distributions.push({
          sgr_code: b.sgr.code,
          product_line_code: r.product_line_code,
          currency_code: r.currency_code,
          value: r.value,
          block_total_ars: totals.ars,
          block_total_usd: totals.usd,
          is_manual_block_total: b.toggleManual,
        });
      }
    }

    const movilBlock = blocks.find((b) => b.sgr.is_movil);
    const movilRows = movilBlock ? resolveBlockRows(movilBlock) : [];
    const movilHasAmount = movilBlock ? blockHasPositiveAmount(movilBlock, localCurrencyCode) : false;

    const products = movilRows
      .filter((r) => r.value > 0)
      .map((r) => ({
        product_line_code: r.product_line_code,
        amount: { currency_code: r.currency_code, value: r.value },
      }));

    // Los totales deben ser la suma de los block totals por SGR (manual o sumado)
    const totalARS = round2(
      blocks.reduce((s, b) => s + blockTotals(b, localCurrencyCode).ars, 0),
    );
    const totalUSD = round2(
      blocks.reduce((s, b) => s + blockTotals(b, localCurrencyCode).usd, 0),
    );

    const statusCode =
      movilHasAmount
        ? isInstrumentationEnabled
          ? APPLICATION_STATUS.instrumentation.label
          : APPLICATION_STATUS.finished.label
        : APPLICATION_STATUS.finished.label;

    onSubmit({
      comment,
      statusCode,
      approved_products: {
        products,
        manual_approved_total_amount_ars: totalARS,
        manual_approved_total_amount_usd: totalUSD,
      },
      sgr_distributions,
    });
  };

  if (!movilCatalogEntry) {
    return null;
  }

  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      title='Aprobar solicitud'
      width='960px'
      overflow='auto'
      primaryText='Aprobar solicitud'
      primaryVariant='primary'
      secondaryText='Cancelar'
      secondaryVariant='secondary'
      onSecondaryAction={onClose}
      onPrimaryAction={handlePrimary}
      primaryActionDisabled={!formValid}
    >
      <Card margin={{ bottom: '2' }}>
        <P type='2' variant='neutral' withMargin={false}>
          Distribuí los montos avalados por cada SGR. La SGR Movil define el
          estado de la carpeta (instrumentación o cierre).
        </P>
      </Card>

      {usdHistoricLocked && (
        <Card
          display='flex'
          margin={{ bottom: '2' }}
          spacing='3'
          variant='warning'
          modifier='weak'
          gap='8px'
          borderRadiusAmount='4'
        >
          <Icon name='warning_triangle_outline' variant='warning' width={24} height={24} />
          <P type='2' variant='neutral' withMargin={false}>
            {USD_HISTORIC_LOCK_BANNER_TEXT}
          </P>
        </Card>
      )}

      {submitError && (
        <Card margin={{ bottom: '2' }}>
          <P type='2' variant='danger' withMargin={false}>
            {submitError}
          </P>
        </Card>
      )}

      <Card margin={{ bottom: '2' }}>
        <P type='3' variant='neutral' weight='bold' withMargin={false}>SGR avaladora</P>
      </Card>

      {blocks.map((block) => {
        const totals = blockTotals(block, localCurrencyCode);
        const takenElsewhere = new Set(
          blocks.filter((b) => b.id !== block.id).map((b) => b.sgr.code),
        );
        const sgrItems = enabledCatalog
          .filter((c) => !takenElsewhere.has(c.code) || c.code === block.sgr.code)
          .map((c) => ({ label: c.name }));

        return (
          <Card
            key={block.id}
            margin={{ bottom: '2' }}
            border
            spacing='3'
          >
            {/* Header del acordeón — siempre visible */}
            <Card display='flex' alignItems='center' gap='8px'>
              {/* Dropdown SGR — stopPropagation para no colapsar al interactuar */}
              <Card width='45%' onClick={(e: React.MouseEvent) => e.stopPropagation()}>
                <DropDown
                  id={`sgr-dd-${block.id}`}
                  title={block.sgr.name}
                  items={sgrItems}
                  onChange={(label) => {
                    const next = enabledCatalog.find((c) => c.name === label);
                    if (next) updateBlock(block.id, { sgr: next });
                  }}
                  size='md'
                />
              </Card>

              {/* Quitar SGR */}
              <Card onClick={(e: React.MouseEvent) => e.stopPropagation()}>
                {blocks.length > 1 && !block.sgr.is_movil ? (
                  <Icon
                    name='delete_outline'
                    variant='neutral'
                    cursor='pointer'
                    onClick={() => removeBlock(block.id)}
                    title='Quitar SGR'
                  />
                ) : (
                  <Card width='24px' />
                )}
              </Card>

              {/* Resumen colapsado — clickeable para expandir/colapsar */}
              <Card
                display='flex'
                alignItems='center'
                gap='8px'
                onClick={() => toggleExpand(block.id)}
                style={{ cursor: 'pointer', flex: 1 }}
              >
                <P type='3' variant='neutral' withMargin={false}>
                  {block.products.length} {block.products.length === 1 ? 'producto' : 'productos'}
                </P>
                <P type='3' variant='neutral' withMargin={false}>
                  {localCurrencyCode} {formatCurrency(String(totals.ars))} / {CURRENCY_CODES.usd} {formatCurrency(String(totals.usd))}
                </P>
                <Card style={{ marginLeft: 'auto' }}>
                  <Icon
                    name={block.isExpanded ? 'chevron_up_outline' : 'chevron_down_outline'}
                    variant='neutral'
                    cursor='pointer'
                  />
                </Card>
              </Card>
            </Card>

            {/* Cuerpo expandible */}
            {block.isExpanded && (
              <>
                {/* Columnas header + filas de productos - siempre visibles con Monto */}
                <Card display='flex' gap='8px' margin={{ top: '3', bottom: '1' }}>
                  <Card width='45%'>
                    <P type='3' variant='neutral' weight='bold' withMargin={false}>Producto</P>
                  </Card>
                  <Card width='20%'>
                    <P type='3' variant='neutral' weight='bold' withMargin={false}>Moneda</P>
                  </Card>
                  <Card width='27%'>
                    <P type='3' variant='neutral' weight='bold' withMargin={false}>Monto</P>
                  </Card>
                  <Card width='40px' />
                </Card>
                {block.products.map((row) => (
                  <Card
                    key={row.id}
                    display='flex'
                    gap='8px'
                    alignItems='center'
                    margin={{ bottom: '2' }}
                  >
                    <Card width='45%'>
                      <DropDown
                        id={`pl-${row.id}`}
                        title={productLineDropdownTitle(productLines, row.product_line_code)}
                        items={productLineItems}
                        onChange={(label) => {
                          const match = productLines.find((pl) => pl.label === label);
                          if (match) updateProductRow(block.id, row.id, { product_line_code: match.code });
                        }}
                        size='sm'
                        disabled={usdHistoricLocked}
                      />
                    </Card>
                    <Card width='20%'>
                      <DropDown
                        id={`cur-${row.id}`}
                        title={row.currency_code}
                        items={currencyOptions}
                        onChange={(label) => updateProductRow(block.id, row.id, { currency_code: label })}
                        size='sm'
                        disabled={usdHistoricLocked}
                      />
                    </Card>
                    <Card width='27%'>
                      <Input
                        id={`amt-${row.id}`}
                        type='number'
                        size='md'
                        value={String(row.amount)}
                        onChange={(e) =>
                          updateProductRow(block.id, row.id, { amount: parseFloat(e.target.value) || 0 })
                        }
                        disabled={usdHistoricLocked}
                      />
                    </Card>
                    <Card width='40px' display='flex' alignItems='center' justifyContent='center'>
                      {block.products.length > 1 && !usdHistoricLocked && (
                        <Icon
                          name='delete_outline'
                          variant='neutral'
                          cursor='pointer'
                          onClick={() => removeProductRow(block.id, row.id)}
                        />
                      )}
                    </Card>
                  </Card>
                ))}

                {/* Footer: agregar producto + toggle */}
                <Card display='flex' justifyContent='space-between' alignItems='center' margin={{ top: '2' }}>
                  {!usdHistoricLocked && (
                    <Button variant='tertiary' size='sm' onClick={() => addProductRow(block.id)}>
                      + Agregar producto
                    </Button>
                  )}
                  {usdHistoricLocked && <Card />}
                  <Toggle
                    id={`toggle-${block.id}`}
                    checked={!block.toggleManual}
                    leftText='Ingresar monto total'
                    rightText='Sumar montos'
                    onChange={(checked: boolean) => updateBlock(block.id, { toggleManual: !checked })}
                    size='sm'
                    disabled={usdHistoricLocked}
                  />
                </Card>

                {/* Total del bloque - cambia según el toggle */}
                <Card margin={{ top: '3' }}>
                  <Card
                    border
                    display='flex'
                    variant={clientTheme ? 'primary' : 'success'}
                    modifier='weak'
                    spacing='3'
                    justifyContent='space-between'
                    alignItems='center'
                  >
                    <Card width='70%'>
                      <H type='3'>Total a aprobar en {block.sgr.name}:</H>
                    </Card>
                    {/* Sumar montos: muestra totales calculados */}
                    {!block.toggleManual ? (
                      <Card width='30%'>
                        <H type='3' withMargin={false} weight='bold' variant={clientTheme ? 'primary' : 'success'}>
                          {localCurrencyCode} {formatCurrency(String(totals.ars))}
                        </H>
                        <H type='3' withMargin={false} weight='bold' variant={clientTheme ? 'primary' : 'success'}>
                          USD {formatCurrency(String(totals.usd))}
                        </H>
                      </Card>
                    ) : (
                      /* Ingresar monto total: campos editables (estilo legacy transparente) */
                      <Card width='30%'>
                        <Card display='flex' gap='8px'>
                          <H type='3' withMargin={false} weight='bold' variant={clientTheme ? 'primary' : 'success'}>
                            {localCurrencyCode}{' '}
                          </H>
                          <NumericFormat
                            placeholder='Ingresá un monto'
                            type='text'
                            id={`mars-${block.id}`}
                            value={block.manualAmountArs}
                            variant='transparent'
                            maxLength={41}
                            thousandSeparator='.'
                            decimalSeparator=','
                            onValueChange={(values) => updateBlock(block.id, { manualAmountArs: values.floatValue ?? 0 })}
                            customInput={Input}
                            decimalScale={2}
                            allowNegative={false}
                            disabled={usdHistoricLocked}
                          />
                        </Card>
                        <Card display='flex' gap='8px'>
                          <H type='3' withMargin={false} weight='bold' variant={clientTheme ? 'primary' : 'success'}>
                            USD{' '}
                          </H>
                          <NumericFormat
                            placeholder='Ingresá un monto'
                            type='text'
                            id={`musd-${block.id}`}
                            value={block.manualAmountUsd}
                            variant='transparent'
                            maxLength={41}
                            thousandSeparator='.'
                            decimalSeparator=','
                            onValueChange={(values) => updateBlock(block.id, { manualAmountUsd: values.floatValue ?? 0 })}
                            customInput={Input}
                            decimalScale={2}
                            allowNegative={false}
                            disabled={usdHistoricLocked || !usdAvailable}
                          />
                        </Card>
                      </Card>
                    )}
                  </Card>
                </Card>
              </>
            )}
          </Card>
        );
      })}

      {canAddSgr && (
        <Card margin={{ top: '2', bottom: '3' }}>
          <Button variant='secondary' size='lg' onClick={addSgrBlock}>
            + Sumar otra SGR al aval
          </Button>
        </Card>
      )}

      <Card margin={{ top: '3' }}>
        <Card
          display='flex'
          variant={clientTheme ? 'primary' : 'success'}
          modifier='regular'
          spacing='3'
          justifyContent='center'
          alignItems='center'
        >
          <div style={{ display: 'grid', gridTemplateColumns: 'auto auto', columnGap: '12px', rowGap: '4px', alignItems: 'center' }}>
            <div style={{ textAlign: 'right' }}>
              <H type='3' withMargin={false} modifier='inverted' align='right'>Total avalado:</H>
            </div>
            <div>
              <H type='3' withMargin={false} weight='bold' modifier='inverted'>
                {localCurrencyCode} {formatCurrency(String(grandTotals.ars))}
              </H>
            </div>
            <div style={{ textAlign: 'right' }}>
              <P type='3' withMargin={false} variant='neutral' modifier='inverted' align='right'>
                Suma de todas las SGR
              </P>
            </div>
            <div>
              <H type='3' withMargin={false} weight='bold' modifier='inverted'>
                USD {formatCurrency(String(grandTotals.usd))}
              </H>
            </div>
          </div>
        </Card>
      </Card>

      <Card margin={{ top: '4' }}>
        <TextArea
          label='Comentario'
          placeHolder='Comentario para el productor'
          maxLengthHelpText={false}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
        />
      </Card>
    </Modal>
  );
}

export default MultiSgrApprovalModal;
